import React from "react";
import {
  Autocomplete,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useFilterBarContext } from "./filterBarContext";
import { FILTER_INPUT_HEIGHT, FILTER_WIDTH } from "./FilterField";

export interface FilterAutocompleteProps {
  source: string;
  label: string;
  /** Selectable values (also the filter value sent to the list). */
  options: string[];
  sx?: SxProps<Theme>;
}

/**
 * Searchable combo filter — same filter-bar wiring as FilterField, but the value
 * is picked from a list (with type-to-search) instead of free text.
 */
export const FilterAutocomplete: React.FC<FilterAutocompleteProps> = ({
  source,
  label,
  options,
  sx,
}) => {
  const { pendingValues, setPendingValue, registerLabel } =
    useFilterBarContext();
  const value = (pendingValues[source] as string | undefined) ?? "";

  React.useEffect(() => {
    registerLabel(source, label);
  }, [source, label, registerLabel]);

  return (
    <Autocomplete
      options={options}
      value={value || null}
      onChange={(_, v) => setPendingValue(source, v ?? "")}
      size="small"
      autoHighlight
      clearOnEscape
      sx={{
        width: { xs: "100%", sm: FILTER_WIDTH },
        flexShrink: 0,
        "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
          minHeight: FILTER_INPUT_HEIGHT,
          py: 0,
        },
        ...(sx as object),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
};

export default FilterAutocomplete;
